import React, {memo, useContext, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import {getTabSessionId} from '@/app/helpers/getTabSessionId';
import {RenderConditionContext} from '@/app/contexts/RenderConditionContext';
import {useScriptInjector} from '@/common/services/headTagger';
import {sendStats} from '@/common/helpers/stats/sendStats';
import {generateRandomSafeBase64} from '@/common/helpers/utils/generateRandomBase64';
import {useAdRefresh} from '@/app/components/common/advertising';
import {useBannerViewability} from '@/app/components/common/advertising/useBannerViewability';
import styles from './AdsenseAdBanner.css';

interface AdsenseAdBannerProps {
	className?: string;
	client: string;
	slot: string;
	width?: number;
	height?: number;
	extraAttributes?: Record<string, string>;
	collapseEmpty?: boolean;
	analyticsLabel: string;
	interfaceName: string;
	placeholder?: boolean;
}

export const AdsenseAdBanner = memo<AdsenseAdBannerProps>(props => {
	const {
		className,
		client,
		slot,
		width,
		height,
		extraAttributes,
		collapseEmpty,
		analyticsLabel,
		interfaceName,
		placeholder,
	} = props;

	const [placeholderShown, setPlaceholderShown] = useState(!!placeholder);
	const renderConditionRef = useContext(RenderConditionContext);
	const isInitialRender = useMemo(() => renderConditionRef.current.isInitial, []);
	const loadingIdRef = useRef<string>('');
	loadingIdRef.current ||= generateRandomSafeBase64(16);
	const showIndexRef = useRef(0);

	const rootRef = useRef<HTMLModElement>(null);
	const isViewableEnoughRef = useBannerViewability(rootRef, interfaceName);

	useScriptInjector({
		src: `//pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`,
		async: true,
	});

	useEffect(() => {
		try {
			((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
		} catch (error) {
			console.warn('[AdsenseAdBanner] adsbygoogle.push() failed', error);
			return;
		}

		const impressionIndex = showIndexRef.current === 0 && isInitialRender ? 0 : 1;

		sendStats(`ad/${analyticsLabel}/display/as/${slot}`);
		sendStats(
			`comm/v1/${Date.now()}/display/${loadingIdRef.current}/${getTabSessionId()}/${interfaceName}/${impressionIndex}/as/${analyticsLabel}/${slot}`,
		);

		let prevStatus: string | undefined;
		const mutationObserver = new MutationObserver(() => {
			const status = rootRef.current!.dataset.adStatus;
			if (!status || status === prevStatus) {
				prevStatus = status;
				return;
			}
			prevStatus = status;
			sendStats(`ad/${analyticsLabel}/shown/${status === 'unfilled' ? 'empty' : 'filled'}/as/${slot}`);
			sendStats(
				`comm/v1/${Date.now()}/shown/${loadingIdRef.current}/${status === 'filled' ? 'filled' : 'empty'}`,
			);

			if (placeholderShown) {
				setPlaceholderShown(false);
			}
		});

		mutationObserver.observe(rootRef.current!, {
			attributeFilter: ['data-ad-status'],
			attributes: true,
		});

		return () => {
			mutationObserver.disconnect();
		};
	}, []);

	useAdRefresh(() => {
		if (!isViewableEnoughRef.current) {
			return;
		}

		loadingIdRef.current = generateRandomSafeBase64(16);
		showIndexRef.current += 1;
		const impressionIndex = showIndexRef.current + 1;

		// Used this dirt workaround to refresh the slot with a new  banner
		const rootNode = rootRef.current!;
		const parent = rootNode.parentElement!;
		rootNode.dataset.adStatus = '';
		parent.removeChild(rootNode);
		parent.appendChild(rootNode);

		sendStats(
			`comm/v1/${Date.now()}/display/${loadingIdRef.current}/${getTabSessionId()}/${interfaceName}/${impressionIndex}/as/${analyticsLabel}/${slot}`,
		);
	}, []);

	return (
		<ins
			ref={rootRef}
			className={cn(
				'adsbygoogle',
				styles.root,
				placeholderShown && styles.withPlaceholder,
				collapseEmpty && styles.collapseEmpty,
				className,
			)}
			style={{
				width,
				height,
			}}
			data-ad-client={client}
			data-ad-slot={slot}
			{...extraAttributes}
		/>
	);
});

AdsenseAdBanner.displayName = 'AdsenseAdBanner';
